/**
 * ResourceUsageWidget — Gauge displays for resource usage
 * Shows: VM disk/RAM/CPU, Supabase usage, API credits remaining
 * Data source: abet-metrics-collector /status
 * Refresh: 30s
 * CSS prefix: ruw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchMetricsStatus } from '../lib/api'
import { Gauge } from 'lucide-react'
import './ResourceUsageWidget.css'

function GaugeCard({ label, value, max, unit, subtitle }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  const color = pct > 85 ? '#ef4444' : pct > 70 ? '#eab308' : '#22c55e'

  return (
    <div className="ruw-gauge">
      <div className="ruw-gauge-label">{label}</div>
      <div className="ruw-gauge-value" style={{ color }}>
        {value != null ? `${typeof value === 'number' ? value.toFixed(1) : value}${unit || ''}` : '—'}
      </div>
      <div className="ruw-gauge-bar">
        <div className="ruw-gauge-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      {subtitle && <div className="ruw-gauge-sub">{subtitle}</div>}
    </div>
  )
}

export default function ResourceUsageWidget() {
  const [snapshot, setSnapshot] = useState(null)

  const fetchData = useCallback(async () => {
    const res = await fetchMetricsStatus()
    if (res.status === 'ok' && res.data?.snapshot) {
      setSnapshot(res.data.snapshot)
    }
  }, [])

  useEffect(() => {
    fetchData()
    const id = setInterval(fetchData, 30000)
    return () => clearInterval(id)
  }, [fetchData])

  const vm = snapshot?.vm || {}
  const api = snapshot?.theoddsapi || {}
  const oe = snapshot?.odds_engine || {}

  return (
    <div className="ruw-container">
      <div className="ruw-header">
        <Gauge size={16} color="#94a3b8" />
        <h3>Resource Usage</h3>
      </div>
      <div className="ruw-grid">
        <GaugeCard
          label="VM Disk"
          value={vm.disk_usage_pct?.value}
          max={100}
          unit="%"
          subtitle="Oracle Cloud VM"
        />
        <GaugeCard
          label="VM Memory"
          value={vm.memory_usage_pct?.value}
          max={100}
          unit="%"
          subtitle="RAM usage"
        />
        <GaugeCard
          label="VM CPU"
          value={vm.cpu_usage_pct?.value}
          max={100}
          unit="%"
          subtitle="CPU utilization"
        />
        <GaugeCard
          label="API Credits"
          value={api.api_credits_pct?.value}
          max={100}
          unit="%"
          subtitle={api.api_credits_used?.value != null
            ? `${api.api_credits_used.value} / ${api.api_credits_capacity?.value || '?'} used`
            : 'TheOddsAPI 9-key rotation'}
        />
        <GaugeCard
          label="Active Books"
          value={oe.active_book_count?.value}
          max={oe.total_book_count?.value || 50}
          unit=""
          subtitle={oe.stale_book_count?.value > 0
            ? `${oe.stale_book_count.value} stale (>30min)`
            : 'All books fresh'}
        />
        <GaugeCard
          label="Ingest Rate"
          value={oe.ingest_rate_per_min?.value}
          max={1000}
          unit="/min"
          subtitle="Records ingested per minute"
        />
      </div>
    </div>
  )
}
